import { FC, useEffect, useState } from 'react';
import {
  IconEditOrders,
  IconMenuDots,
  IconUpdateArrow,
  IconWarning
} from '../../../../../../../assets/DesignSystem/SVGComponents';
import { NudosButton } from '../../../../../../../components/NudosComponents';
import CheckArrowIcon from '../../../../../../../assets/images/catalogue/CheckArrowIcon';
import { ConfirmEditModal } from '../ConfirmEditModal';
import NudosEditAddressModal from '../../../../../../../components/NudosComponents/NudosEditAddressModal/NudosEditAddressModal';
import {
  IOptionsReportList,
  IShipmentData,
  ORDER_STATUS_NAME,
  REPORT_OPTIONS,
  REPORT_STATUS,
  SHIPMENT_PLATFORM,
  SHIPMENT_STATUS
} from '../../../../../../../types/orders';
import SendProductsToWarehouseModal from '../SendProductsToWarehouseModal/SendProductsToWarehouseModal';
import { defaultPlace } from '../../../../../../../utils/defaultPlace';
import { ComponentConfirmEdition } from '../ComponentConfirmEdition';
import './EditingComponent.scss';

interface IEditingComponent {
  orderId: number;
  shipInfo: IShipmentData;
  organizationId: number;
  organizationName?: string;
  updateWithoutLoaders: () => void;
  customEditingButtonSize?: boolean;
  listOfGroupedShipments?: IShipmentData[];
  updateWithLoaders?: () => void;
}

const EditingComponent: FC<IEditingComponent> = ({
  orderId,
  shipInfo,
  organizationId,
  updateWithoutLoaders,
  customEditingButtonSize = false,
  organizationName = 'Nombre de empresa',
  listOfGroupedShipments,
  updateWithLoaders = () => {}
}: IEditingComponent) => {
  const { status, reports, editShipments } = shipInfo;
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [openEditingModal, setOpenEditingModal] = useState<boolean>(false);
  const [openReportList, setOpenReportList] = useState<boolean>(false);
  const [listOfSelectedItems, setListOfSelectedItems] = useState<IOptionsReportList[]>([]);
  const [openMenuExtras, setOpenMenuExtras] = useState<boolean>(false);
  const [openModalSendProductsToWarehouse, setOpenModalSendProductsToWarehouse] = useState<boolean>(false);
  const statusForReportAndEdit = status !== SHIPMENT_STATUS.CANCELLED && status !== SHIPMENT_STATUS.DELIVERED;
  const reportResolved = reports.find(report => report?.status === REPORT_STATUS.RESOLVED);
  const editFromPlatform = editShipments?.platform === SHIPMENT_PLATFORM.NODI;

  const showComponentConfirmEdition = statusForReportAndEdit && (!!reportResolved || editFromPlatform);

  const options: IOptionsReportList[] = [
    { id: 1, label: 'Número de teléfono', value: REPORT_OPTIONS.PHONE_NUMBER },
    { id: 2, label: 'Ciudad', value: REPORT_OPTIONS.CITY },
    { id: 3, label: 'Dirección', value: REPORT_OPTIONS.ADDRESS },
    { id: 4, label: 'Indicaciones', value: REPORT_OPTIONS.ADDITIONAL_REFERENCES },
    { id: 5, label: 'Código Postal', value: REPORT_OPTIONS.ZIP_CODE }
  ];

  const statusCancelOrDelivered =
    (!!listOfGroupedShipments && listOfGroupedShipments[0]?.status.toLowerCase() === ORDER_STATUS_NAME.CANCELLED) ||
    (!!listOfGroupedShipments && listOfGroupedShipments[0]?.status.toLowerCase() === ORDER_STATUS_NAME.DELIVERED);
  const handleOptionsList = (selectedOption: IOptionsReportList) => {
    const optionFound = listOfSelectedItems?.find(option => option.id === selectedOption.id);
    if (optionFound) {
      const deleteElement = listOfSelectedItems?.filter(option => option.id !== selectedOption.id);
      setListOfSelectedItems([...deleteElement]);
    } else {
      setListOfSelectedItems([...listOfSelectedItems, selectedOption]);
    }
  };

  const closeModal = () => {
    setOpenModal(false);
    setOpenEditingModal(false);
  };

  const clearSelectedList = () => setListOfSelectedItems([]);

  const optionsMenuExtras = [
    {
      id: 1,
      label: 'Enviar productos a bodega',
      icon: <IconUpdateArrow />,
      onClick: () => setOpenModalSendProductsToWarehouse(true)
    }
  ];

  const shipmentPlace = defaultPlace(shipInfo);
  const shipmentFlag = shipInfo?.country?.flag;
  const nameCountry = shipInfo?.country?.name;

  useEffect(() => {
    if (openModalSendProductsToWarehouse) {
      setOpenMenuExtras(false);
    }
  }, [openModalSendProductsToWarehouse]);

  return (
    <div id="editingComponent">
      <div className="editingContainer">
        {showComponentConfirmEdition && (
          <ComponentConfirmEdition
            updateWithoutLoaders={updateWithoutLoaders}
            shipmentId={shipInfo.shipmentId}
            reports={shipInfo?.reports || []}
            editShipmentId={shipInfo?.editShipments?.id}
          />
        )}
        <div className="reportEditContainer" onClick={() => setOpenEditingModal(true)}>
          <div className={customEditingButtonSize ? 'newBlueText' : 'blueText'}>Editar información</div>
          <div className="editIconContainer">
            <IconEditOrders />
          </div>
        </div>
        <div className="line"></div>
        <div className="reportEditContainer">
          {openReportList && (
            <div className="optionsList">
              {options?.map((item, index) => {
                const selectedItem = listOfSelectedItems.find(selectedItem => selectedItem?.id === item?.id);
                const numberElements = options?.length;
                return (
                  <div
                    className={`itemList ${numberElements === index + 1 ? 'lastElement' : ''}`}
                    key={`element-list-${item?.id}-index-${index}`}
                    onClick={() => handleOptionsList(item)}
                  >
                    <div className="checkContainer">
                      {selectedItem ? (
                        <div className="selected">
                          <CheckArrowIcon className="resizeIcon" />
                        </div>
                      ) : (
                        <div className="notSelected" />
                      )}
                    </div>
                    <div className="optionText"> {item?.label}</div>
                  </div>
                );
              })}
              <div className="buttonContainer">
                <NudosButton
                  handleClick={() => {
                    setOpenReportList(false);
                    setOpenModal(true);
                  }}
                  isButtonDisabled={listOfSelectedItems.length === 0}
                  customHeight="18px"
                  customWidth="80px"
                  customClassName="minButton"
                  buttonText="Continuar"
                />
              </div>
            </div>
          )}
          <div
            className={customEditingButtonSize ? 'newOrangeText' : 'orangeText'}
            onClick={() => setOpenReportList(!openReportList)}
          >
            Reportar problemas
          </div>
          <div className="iconContainerWarning" onClick={() => setOpenReportList(!openReportList)}>
            <IconWarning stroke="#070F21" />
          </div>
        </div>
        <div className="line"></div>
        <div className="dotsMenu" onClick={() => setOpenMenuExtras(!openMenuExtras)}>
          <IconMenuDots />
        </div>
        {openModal && listOfSelectedItems && (
          <ConfirmEditModal
            selectedOptions={listOfSelectedItems}
            closeModal={closeModal}
            orderId={orderId}
            shipInfo={shipInfo}
            clearSelectedList={clearSelectedList}
            updateWithoutLoaders={updateWithoutLoaders}
          />
        )}
        {openEditingModal && (
          <NudosEditAddressModal
            closeModalCallback={() => closeModal()}
            shipInfo={shipInfo}
            organizationId={organizationId}
            updateWithoutLoaders={updateWithoutLoaders}
          />
        )}
      </div>
      {openMenuExtras && !statusCancelOrDelivered && (
        <div className="containerExtrasMenu">
          {optionsMenuExtras.map((option, index) => (
            <div
              className="itemMenu"
              onClick={() => option.onClick()}
              key={`
              option-${option.id}-index-${index}
            `}
            >
              <div className="iconContainer">{option.icon}</div>
              <p className="itemMenuText">{option.label}</p>
            </div>
          ))}
        </div>
      )}
      {openModalSendProductsToWarehouse && (
        <SendProductsToWarehouseModal
          changeShowModal={() => setOpenModalSendProductsToWarehouse(!openModalSendProductsToWarehouse)}
          orderId={orderId}
          organizationName={organizationName}
          shipmentPlace={shipmentPlace}
          shipmentFlag={shipmentFlag}
          nameCountry={nameCountry}
          listOfGroupedShipments={listOfGroupedShipments}
          updateWithLoaders={updateWithLoaders}
        />
      )}
    </div>
  );
};

export default EditingComponent;
